import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { Divider, Field, FluentProvider, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowLeft16Regular } from "@fluentui/react-icons";
import { NewformaButton } from "../../_base/Button";
import { NewformaLink } from "../../_base/Link";
import { NewformaDatePicker } from "../../_base/NewformaDatePicker";
import { useTheme } from "../../../hooks/useTheme";
import { DateHelper } from "../../../helpers/DateHelpers";
import { FormValidationHelpers } from "../../../helpers/FormValidationHelpers";
import { IPersonaProps } from "../../shared/assignee/AssigneeFluent9";
import FromAndToFluent9Simple from "../../shared/fromAndToComponent/FromAndToFluent9Simple";
import {
    ReminderFluent9,
    getMaxReminderDays,
    MIN_REMINDER_DAYS,
    useCurrentLocalDay,
} from "../../shared/reminder/ReminderFluent9";

const useStyles = makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        userSelect: "none",
        backgroundColor: tokens.colorNeutralBackground1,
    },
    // Mirrors the legacy "newforma-frozen-subheader" Separator: bold, uppercase, start-aligned
    // label on a full-width Divider line. Divider defaults to flexGrow: 1 and renders a short
    // leading stub line before "start"-aligned text; both are overridden here since merging a
    // custom makeStyles class through className reliably replaces the matching built-in atomic
    // class (confirmed empirically), so the text sits flush at the header's own left padding.
    header: {
        flexGrow: 0,
        flexShrink: 0,
        fontWeight: tokens.fontWeightBold,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorNeutralForeground1,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        // 15px top/bottom matches the legacy ".newforma-frozen-subheader" padding (15px 24px),
        // which sets the gap above the header and between the header and the back link below it.
        padding: `15px ${tokens.spacingHorizontalXXL} 15px ${tokens.spacingHorizontalXXL}`,
        "::before": {
            maxWidth: "0px",
            minWidth: "0px",
            marginRight: "0px",
            borderTopWidth: "0px",
        },
    },
    // NewformaButton (Fluent Button) has a built-in 1px border plus spacingHorizontalM (12px)
    // left padding before its icon; 11px here cancels that out so the arrow icon's left edge
    // lands exactly under the header text's left edge (header's spacingHorizontalXXL padding).
    backLink: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalXS,
        flexShrink: 0,
        width: "fit-content",
        marginLeft: "11px",
        marginTop: 0,
    },
    content: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: `4px ${tokens.spacingHorizontalXXL} 0 ${tokens.spacingHorizontalXXL}`,
        // Tighter than the main RFI page's field-to-field gap: this page currently has only the due
        // date and its reminder, which read as one closely related group rather than separate fields.
        gap: tokens.spacingVerticalS,
    },
    footer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
        flexShrink: 0,
        paddingTop: tokens.spacingVerticalM,
        paddingBottom: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalXXL,
        paddingRight: tokens.spacingHorizontalXXL,
        backgroundColor: tokens.colorNeutralBackground1,
    },
    clearFormLink: {
        marginRight: "auto",
    },
    dateField: {
        minWidth: 0,
        // Tightens Fluent's default 4px/4px padding around the required-field asterisk so it sits
        // closer to the "Due Back Date" label text.
        "& .fui-Label__required": {
            paddingLeft: tokens.spacingHorizontalXXS,
            paddingRight: 0,
        },
    },
    datePicker: {
        width: "100%",
        minWidth: 0,
    },
});

export interface ForwardRfiFluent9Props extends LocalizeContextProps {
    isFiling?: boolean;
    /** Owned by the caller so the field survives this panel being unmounted (e.g. on Back). */
    dueDate: Date | undefined;
    onDueDateChange: (date: Date | undefined) => void;
    isReminderEnabled: boolean;
    onReminderEnabledChange: (checked: boolean) => void;
    reminderDays: number;
    onReminderDaysChange: (days: number) => void;
    // Options for the To picker: the full project team, preloaded by the caller (useProjectTeamMembers)
    // and shared with the main RFI page's own From/To pickers rather than fetched again here.
    teamMembers: IPersonaProps[];
    isLoadingTeamMembers?: boolean;
    formValidationHelpers: FormValidationHelpers;
    /** Owned by the caller so the field survives this panel being unmounted (e.g. on Back). */
    toValue: IPersonaProps[];
    onToChange: (people: IPersonaProps[]) => void;
    /** Owned by the caller so the field survives this panel being unmounted (e.g. on Back). */
    ccValue: IPersonaProps[];
    onCcChange: (people: IPersonaProps[]) => void;
    onBack: () => void;
    onClear: () => void;
    onSubmit: () => void;
}

// "Forward for Review" page for the Fluent 9 RFI migration. Remaining fields (purpose, to/cc,
// remarks) and filing behavior will be migrated in a follow-up story; for now this provides the
// page chrome plus the due-back date and reminder fields.
const ForwardRfiFluent9Inner: React.FC<ForwardRfiFluent9Props> = (props) => {
    const styles = useStyles();
    const { theme } = useTheme();
    const isActionDisabled = !!props.isFiling;
    const currentLocalDay = useCurrentLocalDay();

    const { dueDate, reminderDays } = props;
    const maxReminderDays = useMemo(() => getMaxReminderDays(dueDate, undefined, currentLocalDay), [
        dueDate,
        currentLocalDay,
    ]);
    const isReminderAvailable = maxReminderDays >= MIN_REMINDER_DAYS;

    // Due Back Date is required and can't be in the past; the Submit button stays disabled until
    // a valid date is set.
    const isDueDateInvalid = !dueDate || DateHelper.isDateInThePast(dueDate);

    // Reported by FromAndToFluent9Simple (To required, Cc optional, format + to/cc-duplicate checks);
    // starts false since To is required and empty on mount.
    const [isToCcValid, setIsToCcValid] = useState<boolean>(false);

    // Keeps the reminder in sync whenever the due date moves: turns it off once no valid "days
    // before due" remain, and otherwise clamps an already-chosen value down to the new max.
    // wasReminderAutoDisabledRef distinguishes that auto-off from the user deliberately toggling the
    // reminder off, so only the former gets automatically turned back on (mirrors legacy
    // ReminderComponent's componentDidUpdate, which re-enables whenever its `disabled` prop clears).
    const wasReminderAutoDisabledRef = useRef<boolean>(false);
    useEffect(() => {
        if (!isReminderAvailable) {
            props.onReminderEnabledChange(false);
            wasReminderAutoDisabledRef.current = true;
            return;
        }
        if (wasReminderAutoDisabledRef.current) {
            props.onReminderEnabledChange(true);
            wasReminderAutoDisabledRef.current = false;
        }
        const clamped = Math.min(reminderDays, maxReminderDays);
        if (clamped !== reminderDays) {
            props.onReminderDaysChange(clamped);
        }
    }, [maxReminderDays, isReminderAvailable, reminderDays, props.onReminderEnabledChange, props.onReminderDaysChange]);

    return (
        <FluentProvider theme={theme} style={{ display: "contents" }}>
            <div className={styles.root} data-testid="forward-rfi-fluent9">
                <Divider alignContent="start" className={styles.header} data-testid="forward-rfi-header">
                    {props.translate("RFI.FORWARD_RFI.HEADER_TEXT") as string}
                </Divider>
                <NewformaButton
                    appearance="subtle"
                    testid="forward-rfi-back-button"
                    icon={<ArrowLeft16Regular />}
                    onClick={props.onBack}
                    className={styles.backLink}
                >
                    {props.translate("RFI.FORWARD_RFI.BACK_BUTTON") as string}
                </NewformaButton>
                <div className={styles.content}>
                    <Field
                        label={props.translate("RFI.FORWARD_RFI.DUE_BACK_DATE") as string}
                        required={true}
                        className={styles.dateField}
                        data-testid="forward-rfi-due-date-field"
                    >
                        <NewformaDatePicker
                            data-testid="forward-rfi-due-date-picker"
                            className={styles.datePicker}
                            value={dueDate}
                            onSelectDate={(date) => props.onDueDateChange(date ?? undefined)}
                            minDate={currentLocalDay}
                            disabled={isActionDisabled}
                            showGoToToday={true}
                            aria-label={props.translate("RFI.FORWARD_RFI.DUE_BACK_DATE") as string}
                        />
                    </Field>
                    <ReminderFluent9
                        testIdPrefix="forward-rfi-reminder"
                        label={props.translate("SHARED.REMINDER_COMPONENT.TOGGLE") as string}
                        daysLabel={props.translate("SHARED.REMINDER_COMPONENT.DAYS_LABEL") as string}
                        checked={props.isReminderEnabled}
                        onCheckedChange={(checked) => {
                            wasReminderAutoDisabledRef.current = false;
                            props.onReminderEnabledChange(checked);
                        }}
                        switchDisabled={isActionDisabled || !isReminderAvailable}
                        days={reminderDays}
                        onDaysChange={props.onReminderDaysChange}
                        min={MIN_REMINDER_DAYS}
                        max={maxReminderDays}
                        stepperDisabled={isActionDisabled}
                    />
                    <FromAndToFluent9Simple
                        hasFrom={false}
                        hasTo={true}
                        hasCc={true}
                        teamMembers={props.teamMembers}
                        isLoadingTeamMembers={props.isLoadingTeamMembers}
                        formValidationHelpers={props.formValidationHelpers}
                        disabled={isActionDisabled}
                        isToRequired={true}
                        isCcRequired={false}
                        toValue={props.toValue}
                        onToChange={props.onToChange}
                        ccValue={props.ccValue}
                        onCcChange={props.onCcChange}
                        onValidationChange={setIsToCcValid}
                    />
                </div>
                <div className={styles.footer}>
                    <NewformaLink
                        className={styles.clearFormLink}
                        dataTestId="forward-rfi-clear-link"
                        disabled={isActionDisabled}
                        onClick={props.onClear}
                    >
                        {props.translate("FLUENT9.CLEAR_FORM") as string}
                    </NewformaLink>
                    <NewformaButton
                        appearance="primary"
                        testid="forward-rfi-submit-button"
                        disabled={isActionDisabled || isDueDateInvalid || !isToCcValid}
                        onClick={props.onSubmit}
                    >
                        {props.translate("RFI.SUBMIT") as string}
                    </NewformaButton>
                </div>
            </div>
        </FluentProvider>
    );
};

export default withLocalize(ForwardRfiFluent9Inner);
